import { FC, Fragment } from "react";

import { AddBoxIcon, Column, Row } from "@hightouch/htui";
import immutableUpdate from "immutability-helper";

import { toSingleCondition } from "src/components/audiences/utils";
import {
  isRelatedColumn,
  ConditionType,
  TraitCondition,
  createRootPropertyCondition,
  InlineTraitCondition,
  createPropertyCondition,
} from "src/types/visual";

import { FilterProps } from "./condition";
import { DetailButton } from "./condition-buttons";
import { NestedPropertyFilter } from "./nested-property-filter";
import { FilterableOptionsProvider } from "src/components/explore/filter-popover/filterable-options-context";
import { TRAIT_SUBCONDITION_FILTERABLE_SECTIONS } from "src/components/explore/filter-popover/constants";
import { useFilterableOptionsContext } from "src/components/explore/filter-popover/filterable-options-hook";
import { createTraitSubconditionUpdater } from "src/components/explore/utils/condition-builders";

export type TraitInlineSubconditionsProps = FilterProps<
  TraitCondition | InlineTraitCondition
> & { isNested?: boolean };

export const TraitInlineSubconditions: FC<
  Readonly<TraitInlineSubconditionsProps>
> = (props) => {
  // Transform condition from the legacy format to the new format
  // where there is a SINGLE top-level AND/OR condition
  const condition = transformTraitConditions(props.condition);

  const { onUpdate } = props;

  const { relationships } = useFilterableOptionsContext();

  // Trait filters operate on the related model, not the trait parent model
  const traitRelatedModelId = relationships?.find(
    ({ id }) =>
      isRelatedColumn(condition.property) &&
      id.toString() === condition.property.path[0]?.toString(),
  )?.to_model.id;

  const addRootCondition = () => {
    onUpdate((condition) => {
      if ("conditions" in condition.property.column) {
        condition.property.column.conditions = [createRootPropertyCondition()];
      }
    });
  };

  const addCondition = () => {
    onUpdate((condition) => {
      if (
        "conditions" in condition.property.column &&
        condition.property.column.conditions[0] &&
        "conditions" in condition.property.column.conditions[0]
      ) {
        condition.property.column.conditions[0].conditions.push(
          createPropertyCondition() as any,
        );
      }
    });
  };

  const removeCondition = (id?: string) => () => {
    onUpdate((condition) => {
      if (
        "conditions" in condition.property.column &&
        condition.property.column.conditions[0] &&
        "conditions" in condition.property.column.conditions[0]
      ) {
        const conditionsList =
          condition.property.column.conditions[0].conditions;
        conditionsList.splice(
          conditionsList.findIndex((c) => c.id === id),
          1,
        );
      }
    });
  };

  const onToggleWrappingConditionType = (
    newConditionType: ConditionType.And | ConditionType.Or,
  ) => {
    onUpdate((condition) => {
      if (
        "conditions" in condition.property.column &&
        condition.property.column.conditions.length > 0
      ) {
        condition.property.column.conditions[0]!.type = newConditionType;
      }
    });
  };

  // Trait templates may have inline subconditions
  // Traits themselves no longer are allowed to but may have them from legacy configuration.
  // If a trait is not using the legacy configuration, we will display the trait's config conditions readonly here
  const rootConditions =
    "conditions" in condition.property.column
      ? condition.property.column.conditions
      : [];

  if (rootConditions?.length === 0) {
    // This is referring to householding. Nested traits via inline templates ARE allowed to add inline subconditions
    if (props.isNested) {
      return null;
    }

    return <AddConditionButton onClick={addRootCondition} />;
  }

  return (
    <FilterableOptionsProvider
      modelId={traitRelatedModelId}
      sections={TRAIT_SUBCONDITION_FILTERABLE_SECTIONS}
    >
      {rootConditions.map((topLevelCondition, index: number) => (
        <Column key={topLevelCondition.id ?? index} gap={4}>
          {topLevelCondition.conditions.map(
            (subcondition, subindex: number) => (
              <Fragment key={subcondition.id ?? subindex}>
                <Row align="flex-start" width="100%">
                  <NestedPropertyFilter
                    {...props}
                    onUpdate={createTraitSubconditionUpdater(onUpdate)(
                      subcondition.id,
                    )}
                    columns={undefined} // make sure columns are not passed in
                    isFirstCondition={subindex === 0}
                    lhsPropertyFilterVariant="dropdown"
                    condition={subcondition}
                    isWrappedWithAndCondition={
                      topLevelCondition.type === ConditionType.And
                    }
                    onRemove={removeCondition(subcondition.id)}
                    onToggleWrappingConditionType={
                      onToggleWrappingConditionType
                    }
                  />
                </Row>
              </Fragment>
            ),
          )}
          {!props.isNested && <AddConditionButton onClick={addCondition} />}
        </Column>
      ))}
    </FilterableOptionsProvider>
  );
};

type AddConditionButtonProps = {
  onClick: () => void;
};

const AddConditionButton: FC<Readonly<AddConditionButtonProps>> = ({
  onClick,
}) => (
  <DetailButton icon={AddBoxIcon} size="sm" onClick={onClick}>
    Where...
  </DetailButton>
);

const transformTraitConditions = (
  condition: TraitCondition | InlineTraitCondition,
): TraitCondition | InlineTraitCondition => {
  // Transform condition to AND/OR
  const transformedCondition = immutableUpdate(condition, {
    property: {
      column: {
        // Trait conditions
        conditions: {
          $apply: (input) => {
            const rootConditions = toSingleCondition(input);
            return rootConditions ? rootConditions : input;
          },
        },
      },
    },
  });

  return transformedCondition;
};
